import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { X, ExternalLink, Loader2, AlertTriangle, Calendar, User as UserIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * PressReleaseReaderModal — a clean, reader-mode modal that renders the
 * scraped article body INSIDE sevenchapterbooks.com so visitors don't have
 * to leave the site to read press coverage.
 *
 * Flow:
 *   1. Mount with bookId + articleId → fetches
 *      `/api/books/{bookId}/press-releases/{articleId}/content`.
 *   2. Backend extracts the article body via trafilatura. Caches for 24h.
 *   3. If extraction succeeded (`readable: true`), we render the sanitized
 *      HTML inside a serif reader column. Headings, paragraphs, lists, and
 *      images are styled by the `reader-prose` class below.
 *   4. If extraction failed (paywall, JS-only page, bot-blocking) we render
 *      the OG description + a prominent "Open original on {site} →" button
 *      so the user always has a path forward.
 */
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const PressReleaseReaderModal = ({ bookId, article, onClose }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lock background scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch readable content
  useEffect(() => {
    if (!article?.id || !bookId) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/books/${bookId}/press-releases/${article.id}/content`, {
        // The first uncached fetch is heavier (trafilatura runs server-side).
        // Bump the timeout so cold-start preview environments don't reject.
        timeout: 30000,
        _noRetry: true,
      })
      .then((res) => { if (mounted) setContent(res.data || {}); })
      .catch((e) => {
        if (mounted) setError(e?.response?.data?.detail || e?.message || 'Could not load article');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [bookId, article?.id]);

  const safeHtml = useMemo(() => {
    if (!content?.html) return '';
    return DOMPurify.sanitize(content.html, {
      // Keep article-level tags and strip everything else (script/style/iframe etc.)
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'em', 'b', 'i', 'u', 's', 'sup', 'sub', 'small',
        'ul', 'ol', 'li',
        'blockquote', 'q', 'cite', 'figure', 'figcaption',
        'a', 'img',
        'span', 'div',
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel'],
      ADD_ATTR: ['target', 'rel'],
    });
  }, [content?.html]);

  const sourceLabel = content?.source || article?.source || article?.og_site_name || 'source';
  const externalUrl = content?.external_url || article?.external_url;
  const heroImage = content?.hero_image || article?.og_image || null;
  const articleTitle = content?.title || article?.headline || article?.og_title || 'Press release';
  const author = content?.author;
  const published = content?.published || article?.publication_date;
  const isReadable = !!content?.readable && !!safeHtml;
  const fallbackSummary = content?.fallback_summary || article?.og_description || '';

  const modalNode = (
    <div
      className="fixed inset-0 z-[2147483000] flex items-stretch sm:items-center justify-center bg-black/85 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="press-reader-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="press-release-reader-modal"
    >
      <div
        className="relative bg-zinc-950 text-zinc-100 w-full sm:max-w-3xl sm:rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col max-h-screen sm:max-h-[92vh]"
      >
        {/* Sticky header */}
        <div className="flex items-center justify-between gap-3 px-5 sm:px-7 py-3.5 border-b border-white/10 bg-zinc-950 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-fuchsia-300 font-semibold truncate" data-testid="press-reader-source">
              {sourceLabel}
            </p>
            {published && (
              <p className="text-[11px] text-gray-400 mt-0.5 inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {formatDate(published)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="press-reader-open-original"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open original
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
              aria-label="Close article reader"
              data-testid="press-reader-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-testid="press-reader-body">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-300">
              <Loader2 className="h-8 w-8 animate-spin text-fuchsia-400 mb-3" />
              <p className="text-sm">Loading article…</p>
              <p className="text-xs text-gray-500 mt-1">First load takes a moment — we cache it for next time.</p>
            </div>
          )}

          {!loading && error && (
            <div className="px-6 sm:px-10 py-12 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-amber-500/15 text-amber-300 flex items-center justify-center mb-3">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Couldn't load the article</h3>
              <p className="text-sm text-gray-300 max-w-md mx-auto mb-5">{String(error)}</p>
              {externalUrl && (
                <a
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                  data-testid="press-reader-fallback-open"
                >
                  Open on {sourceLabel} <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          )}

          {!loading && !error && (
            <article className="px-5 sm:px-10 py-7 sm:py-10">
              <h1
                id="press-reader-title"
                className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-3"
                data-testid="press-reader-title"
              >
                {articleTitle}
              </h1>
              {(author || published) && (
                <div className="flex items-center gap-3 text-sm text-gray-400 mb-6 flex-wrap">
                  {author && (
                    <span className="inline-flex items-center gap-1.5">
                      <UserIcon className="h-3.5 w-3.5" /> {author}
                    </span>
                  )}
                  {author && published && <span className="text-gray-600">·</span>}
                  {published && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" /> {formatDate(published)}
                    </span>
                  )}
                </div>
              )}

              {heroImage && (
                <figure className="mb-6 rounded-xl overflow-hidden bg-zinc-900 flex items-center justify-center">
                  <img
                    src={heroImage}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-full max-h-[260px] sm:max-h-[320px] object-contain"
                    onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                  />
                </figure>
              )}

              {isReadable ? (
                <div
                  className="reader-prose text-gray-200"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                  data-testid="press-reader-article-html"
                />
              ) : (
                <div className="space-y-5" data-testid="press-reader-fallback">
                  {fallbackSummary && (
                    <p className="text-base text-gray-200 leading-relaxed">{fallbackSummary}</p>
                  )}
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 text-sm text-amber-200/90 leading-relaxed">
                    <p className="font-semibold text-amber-200 mb-1.5">This article can't be rendered inline</p>
                    <p>
                      The publisher's site requires a direct visit (for example, paywalled or
                      JavaScript-rendered pages). Tap below to read it on{' '}
                      <span className="font-semibold">{sourceLabel}</span> — it opens in a new tab.
                    </p>
                  </div>
                  {externalUrl && (
                    <a
                      href={externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      data-testid="press-reader-fallback-cta"
                    >
                      Read on {sourceLabel} <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              )}

              {/* Footer link — always visible at end of article so readers
                  can verify the source and visit if they want more context. */}
              {externalUrl && isReadable && (
                <div className="mt-10 pt-6 border-t border-white/10">
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-fuchsia-300 hover:text-fuchsia-200 font-medium"
                    data-testid="press-reader-footer-original"
                  >
                    View original on {sourceLabel} <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </article>
          )}
        </div>
      </div>
    </div>
  );

  // Render via a portal so we escape any ancestor stacking context (the
  // PressReleaseSection's `backdrop-blur-sm` was trapping the modal under
  // the global Navbar, which is why the article title appeared cropped at
  // the top with the navbar bleeding over it).
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(modalNode, document.body);
};

export default PressReleaseReaderModal;
